const ACTION_WORDS = [
  "strike", "strikes", "attack", "attacks", "slash", "slashes", "thrust", "thrusts",
  "blow", "lunges", "carves", "cleaves", "hits", "hammers", "drives", "cuts", "punishes",
];

const NET_ESCAPE_PATTERNS = [
  "thrashes free of the net",
  "cuts loose from the net",
  "tears the net away",
  "breaks free of the mesh",
  "claws out of the net",
];

const NET_CONTROL_PATTERNS = [
  "casts the weighted net",
  "the net whirls out and snares",
  "ensnares",
  "spins the net expertly, wrapping",
  "weighted net catches",
  "times the net throw perfectly, binding",
  "is caught in the mesh",
  "is tangled and floundering",
];

const DEFENSIVE_BLOCK_PATTERNS = [
  "turns it aside",
  "glancing graze",
  "stands firm",
  "weathers it",
  "shrugs it off",
  "barely bites through",
  "turned aside",
  "absorbs the blow",
  "the attack is denied",
  "holds firm",
  "guards against",
];

const OFFENSIVE_SHIELD_PATTERNS = [
  "shield-bash",
  "shield rim and blade",
  "bulls forward behind the shield",
  "punches the gladius",
  "grinds ",
  "hammers ",
];

function hasAnyPattern(text, patterns) {
  return patterns.some((pattern) => text.includes(pattern));
}

function safeName(battle, side) {
  if (side === "g1") return battle.g1_name || battle.g1 || "Gladiator One";
  return battle.g2_name || battle.g2 || "Gladiator Two";
}

function roundFromLine(line, fallbackRound = 0) {
  const match = String(line || "").match(/(?:^|\s)(?:R|Round)\s*(\d+)/i);
  if (match) return Number(match[1]);
  const heading = String(line || "").match(/Round\s+(\d+)/i);
  if (heading) return Number(heading[1]);
  return fallbackRound;
}

function mentions(line, name) {
  const idx = String(line || "").indexOf(name);
  return idx >= 0 ? idx : Number.POSITIVE_INFINITY;
}

function classifyLine(line) {
  const text = String(line || "");
  const lower = text.toLowerCase();
  const hasActionWord = ACTION_WORDS.some((word) => lower.includes(word));
  if (!text.trim()) return "commentary";
  if (hasAnyPattern(lower, NET_ESCAPE_PATTERNS)) return "net_escape";
  if (lower.includes("victorious") || lower.includes("wins") || lower.includes("triumphant") || lower.includes("claims the day") || lower.includes("has prevailed")) return "victory";
  if (lower.includes("finishing blow") || lower.includes("deciding strike") || lower.includes("fight-ending") || lower.includes("ends it") || lower.includes("seals the bout")) return "finisher";
  if (lower.includes("critical")) return "critical";
  if (lower.includes("bloodied") || lower.includes("end is near") || lower.includes("mortal danger") || lower.includes("moments from defeat") || lower.includes("death's door")) return "bloodied";
  if (lower.includes("stunned") || lower.includes("seeing stars") || lower.includes("too shaken") || lower.includes("cannot answer")) return "stun";
  if (hasAnyPattern(lower, NET_CONTROL_PATTERNS)) return "net";
  if (lower.includes("riposte") || lower.includes("counter")) return "riposte";
  if (lower.includes("follow-up") || lower.includes("surges again") || lower.includes("second strike") || lower.includes("flurry")) return "flurry";
  if (hasActionWord && hasAnyPattern(lower, OFFENSIVE_SHIELD_PATTERNS)) return "strike";
  if (hasAnyPattern(lower, DEFENSIVE_BLOCK_PATTERNS) || lower.includes("glance away")) return "block";
  if (lower.includes("dodge") || lower.includes("evades") || lower.includes("slips") || lower.includes("whistle past") || lower.includes("swinging at shadows") || lower.includes("glides away")) return "dodge";
  if (lower.includes("round ") && !hasActionWord) return "round";
  if (hasActionWord) return "strike";
  return "commentary";
}

function actorTargetForLine(line, battle, type) {
  const text = String(line || "");
  const lower = text.toLowerCase();
  const g1Name = safeName(battle, "g1");
  const g2Name = safeName(battle, "g2");
  const g1Index = mentions(text, g1Name);
  const g2Index = mentions(text, g2Name);
  const g1Seen = Number.isFinite(g1Index);
  const g2Seen = Number.isFinite(g2Index);

  if (type === "victory") {
    const winner = battle.winner_id === battle.g1_id ? "g1" : "g2";
    return { actor: winner, target: winner === "g1" ? "g2" : "g1" };
  }

  if (!g1Seen && !g2Seen) return { actor: null, target: null };

  if (type === "bloodied" || type === "stun") {
    if (g1Seen && (!g2Seen || g1Index < g2Index)) return { actor: "g2", target: "g1" };
    return { actor: "g1", target: "g2" };
  }

  if (type === "net") {
    if (g1Seen && !g2Seen) return { actor: "g2", target: "g1" };
    if (g2Seen && !g1Seen) return { actor: "g1", target: "g2" };
    if (lower.includes("the net whirls out and snares")) {
      if (g1Seen && (!g2Seen || g1Index < g2Index)) return { actor: "g2", target: "g1" };
      if (g2Seen) return { actor: "g1", target: "g2" };
    }
    if (g1Seen && g2Seen) {
      if (g1Index < g2Index) return { actor: "g1", target: "g2" };
      return { actor: "g2", target: "g1" };
    }
    return { actor: null, target: null };
  }

  if (type === "net_escape") {
    if (g1Seen && (!g2Seen || g1Index < g2Index)) return { actor: "g1", target: "g2" };
    if (g2Seen) return { actor: "g2", target: "g1" };
    return { actor: null, target: null };
  }

  if (type === "dodge" || type === "block") {
    if (g1Seen && (!g2Seen || g1Index < g2Index)) return { actor: "g1", target: "g2" };
    return { actor: "g2", target: "g1" };
  }

  if (g1Seen && !g2Seen) return { actor: "g1", target: "g2" };
  if (g2Seen && !g1Seen) return { actor: "g2", target: "g1" };
  if (g1Index < g2Index) return { actor: "g1", target: "g2" };
  return { actor: "g2", target: "g1" };
}

function hpStateAtRound(hpHistory, round) {
  if (!Array.isArray(hpHistory) || hpHistory.length === 0) {
    return { g1_hp: 100, g1_max_hp: 100, g2_hp: 100, g2_max_hp: 100 };
  }
  const state = hpHistory.find((entry) => Number(entry.round) === Number(round));
  return state || hpHistory[hpHistory.length - 1] || hpHistory[0];
}

function damagePct(damage, maxHp) {
  if (!maxHp) return 0;
  return Math.max(0, Math.round((Number(damage || 0) / Number(maxHp || 1)) * 100));
}

function legacyHealthTimeline(events, battle) {
  const finalRound = Math.max(1, ...events.map((event) => Number(event.round) || 0));
  const winnerSide = battle.winner_id === battle.g1_id ? "g1" : "g2";
  const history = [{ round: 0, g1_hp: 100, g1_max_hp: 100, g2_hp: 100, g2_max_hp: 100 }];
  for (let round = 1; round <= finalRound; round += 1) {
    const progress = round / finalRound;
    const winnerHp = Math.max(18, Math.round(100 - 68 * progress));
    const loserHp = Math.max(0, Math.round(100 * (1 - progress)));
    history.push({
      round,
      g1_hp: winnerSide === "g1" ? winnerHp : loserHp,
      g1_max_hp: 100,
      g2_hp: winnerSide === "g2" ? winnerHp : loserHp,
      g2_max_hp: 100,
    });
  }
  return history;
}

export function buildBattleReplay3DData(battle) {
  if (!battle || !Array.isArray(battle.log) || battle.log.length === 0) {
    return { supported: false, reason: "3D battle unavailable. View the standard battle report." };
  }
  if (!battle.g1_id || !battle.g2_id) {
    return { supported: false, reason: "3D battle unavailable. View the standard battle report." };
  }

  let currentRound = 0;
  const events = battle.log.map((line, index) => {
    const nextRound = roundFromLine(line, currentRound);
    currentRound = nextRound;
    const type = classifyLine(line);
    const { actor, target } = actorTargetForLine(line, battle, type);
    return {
      index,
      line,
      round: currentRound,
      type,
      actor,
      target,
    };
  });

  const eventsByRound = {};
  events.forEach((event, index) => {
    if (event.round > 0) {
      if (!eventsByRound[event.round]) eventsByRound[event.round] = [];
      eventsByRound[event.round].push({ event, index });
    }
  });

  const hpHistory = Array.isArray(battle.hp_history) && battle.hp_history.length > 0
    ? battle.hp_history
    : legacyHealthTimeline(events, battle);

  Object.entries(eventsByRound).forEach(([roundKey, roundEntries]) => {
    const round = Number(roundKey);
    const before = hpStateAtRound(hpHistory, Math.max(0, round - 1));
    const after = hpStateAtRound(hpHistory, round);
    const g1Damage = Math.max(0, Number(before.g1_hp || 0) - Number(after.g1_hp || 0));
    const g2Damage = Math.max(0, Number(before.g2_hp || 0) - Number(after.g2_hp || 0));

    const pickStrikeEvent = (targetSide) => {
      const directHit = roundEntries.find(({ event }) => (
        event.target === targetSide
        && ["strike", "flurry", "riposte", "critical", "finisher", "net"].includes(event.type)
      ));
      if (directHit) return directHit;

      const statusHit = roundEntries.find(({ event }) => event.target === targetSide && ["bloodied", "stun"].includes(event.type));
      if (statusHit) return statusHit;

      return roundEntries[roundEntries.length - 1] || null;
    };

    const g1Entry = g1Damage > 0 ? pickStrikeEvent("g1") : null;
    const g2Entry = g2Damage > 0 ? pickStrikeEvent("g2") : null;

    if (g1Entry) {
      g1Entry.event.resolution = {
        round,
        g1Damage,
        g2Damage: g1Entry === g2Entry ? g2Damage : 0,
        after,
      };
    }

    if (g2Entry && g2Entry !== g1Entry) {
      g2Entry.event.resolution = {
        round,
        g1Damage: 0,
        g2Damage,
        after,
      };
    }
  });

  return {
    supported: true,
    fighters: {
      g1: { id: battle.g1_id, name: safeName(battle, "g1"), cls: battle.g1_class || "Provocator" },
      g2: { id: battle.g2_id, name: safeName(battle, "g2"), cls: battle.g2_class || "Provocator" },
    },
    winner_id: battle.winner_id,
    initialHp: hpHistory[0],
    finalHp: hpHistory[hpHistory.length - 1] || hpHistory[0],
    events,
  };
}

export function getBattleReplay3DFrame(replay, shown) {
  if (!replay?.supported) return null;
  const visibleCount = Math.max(0, Math.min(Number(shown || 0), replay.events.length));
  const visibleEvents = replay.events.slice(0, visibleCount);
  const entangled = { g1: false, g2: false };
  let hp = {
    g1_hp: Number(replay.initialHp?.g1_hp || 100),
    g1_max_hp: Number(replay.initialHp?.g1_max_hp || 100),
    g2_hp: Number(replay.initialHp?.g2_hp || 100),
    g2_max_hp: Number(replay.initialHp?.g2_max_hp || 100),
  };
  visibleEvents.forEach((event) => {
    if (event.resolution?.after) hp = event.resolution.after;
    if (event.type === "net" && event.target) entangled[event.target] = true;
    if (event.type === "net_escape" && event.actor) entangled[event.actor] = false;
  });

  const currentEvent = visibleCount > 0
    ? replay.events[Math.min(visibleCount - 1, replay.events.length - 1)]
    : replay.events[0];
  const impact = {
    g1Pct: damagePct(currentEvent?.resolution?.g1Damage, currentEvent?.resolution?.after?.g1_max_hp || replay.initialHp?.g1_max_hp || 100),
    g2Pct: damagePct(currentEvent?.resolution?.g2Damage, currentEvent?.resolution?.after?.g2_max_hp || replay.initialHp?.g2_max_hp || 100),
  };
  impact.targetPct = currentEvent?.target === "g1" ? impact.g1Pct : currentEvent?.target === "g2" ? impact.g2Pct : Math.max(impact.g1Pct, impact.g2Pct);
  impact.maxPct = Math.max(impact.g1Pct, impact.g2Pct, 0);

  const floating = [];
  if (currentEvent?.resolution) {
    const g1MaxHp = Number(currentEvent.resolution.after?.g1_max_hp || replay.initialHp?.g1_max_hp || 100);
    const g2MaxHp = Number(currentEvent.resolution.after?.g2_max_hp || replay.initialHp?.g2_max_hp || 100);
    if (currentEvent.resolution.g1Damage > 0) {
      const pct = Math.max(1, Math.round((currentEvent.resolution.g1Damage / g1MaxHp) * 100));
      floating.push({ side: "g1", text: `-${pct}%`, tone: "damage" });
    }
    if (currentEvent.resolution.g2Damage > 0) {
      const pct = Math.max(1, Math.round((currentEvent.resolution.g2Damage / g2MaxHp) * 100));
      floating.push({ side: "g2", text: `-${pct}%`, tone: "damage" });
    }
  }
  if (currentEvent?.type === "dodge") floating.push({ side: currentEvent.actor, text: "Slip", tone: "status" });
  if (currentEvent?.type === "block") floating.push({ side: currentEvent.actor, text: "Guarded", tone: "status" });
  if (currentEvent?.type === "critical") floating.push({ side: currentEvent.target, text: "Critical", tone: "critical" });
  if (currentEvent?.type === "finisher") floating.push({ side: currentEvent.target, text: "Decisive", tone: "critical" });
  if (currentEvent?.type === "net") floating.push({ side: currentEvent.target, text: "Entangled", tone: "status" });
  if (currentEvent?.type === "net_escape") floating.push({ side: currentEvent.actor, text: "Recovered", tone: "status" });
  if (currentEvent?.type === "stun") floating.push({ side: currentEvent.target, text: "Stunned", tone: "status" });
  if (currentEvent?.type === "victory") floating.push({ side: currentEvent.actor, text: "Victory", tone: "victory" });

  return {
    hp,
    currentEvent,
    impact,
    entangled,
    floating,
    done: visibleCount >= replay.events.length,
  };
}

export function describeBattleReplay3DEvent(replay, frame) {
  const event = frame?.currentEvent;
  const actorName = event?.actor ? replay?.fighters?.[event.actor]?.name : null;
  const targetName = event?.target ? replay?.fighters?.[event.target]?.name : null;

  if (frame?.done) {
    const winner = replay?.fighters?.[event?.actor || (replay?.fighters?.g1?.id === replay?.winner_id ? "g1" : "g2")]?.name || actorName || "The victor";
    return {
      title: `${winner} owns the arena`,
      detail: "The duel is settled. The saved result is now on full display.",
      tone: "victory",
    };
  }

  switch (event?.type) {
    case "finisher":
      return { title: `${actorName || "A gladiator"} commits to the killing blow`, detail: targetName ? `${targetName} is caught in a cinematic finishing strike.` : "The duel reaches its decisive final moment.", tone: "finisher" };
    case "critical":
      return { title: `${actorName || "A gladiator"} lands a brutal critical`, detail: targetName ? `${targetName} absorbs the heaviest impact of the exchange.` : event?.line || "A crushing strike lands cleanly.", tone: "critical" };
    case "strike":
    case "flurry":
    case "riposte":
      return { title: `${actorName || "A gladiator"} drives the exchange`, detail: targetName ? `${targetName} is forced to answer under pressure.` : event?.line || "Steel meets steel in the center of the sand.", tone: "attack" };
    case "block":
      return { title: `${actorName || "A gladiator"} holds firm`, detail: targetName ? `${targetName}'s blow is turned aside by shield and guard.` : event?.line || "The attack is absorbed and denied.", tone: "defense" };
    case "dodge":
      return { title: `${actorName || "A gladiator"} slips clear`, detail: targetName ? `${targetName} misses as the target glides out of danger.` : event?.line || "The strike whistles past empty space.", tone: "defense" };
    case "net":
      return { title: `${actorName || "A gladiator"} takes control`, detail: targetName ? `${targetName} is tangled up and loses the rhythm of the fight.` : event?.line || "A control moment changes the duel's spacing.", tone: "control" };
    case "net_escape":
      return { title: `${actorName || "A gladiator"} breaks clear`, detail: targetName ? `${actorName || "The trapped fighter"} tears free before ${targetName || "the opponent"} can fully capitalize.` : event?.line || "The trapped fighter regains enough freedom to continue.", tone: "control" };
    case "stun":
      return { title: `${targetName || "A gladiator"} is rattled`, detail: actorName ? `${actorName} has the initiative and can press the advantage.` : event?.line || "A clean impact leaves the target staggered.", tone: "control" };
    case "bloodied":
      return { title: `${targetName || "A gladiator"} is on the brink`, detail: actorName ? `${actorName} smells the finish as the crowd rises.` : event?.line || "The duel enters a danger phase.", tone: "critical" };
    case "victory":
      return { title: `${actorName || "The winner"} claims the spectacle`, detail: targetName ? `${targetName} can no longer answer the challenge.` : "One fighter remains standing in the sand.", tone: "victory" };
    case "round":
      return { title: `Round ${event?.round || "?"}`, detail: event?.line || "The duel resets for the next exchange.", tone: "neutral" };
    default:
      return { title: "The crowd roars", detail: event?.line || "Momentum shifts with every heartbeat.", tone: "neutral" };
  }
}
